/*
 * Purpose: Compose Password and OTP UI components for reset/update flows.
 * Boundary: Layout only; no local state; delegates to child components.
 * Invariants:
 * - Displays CreatePassword followed by OTP component.
 * - Shows channel selector chips when 2+ channels available.
 * - Warning text adapts to selected channel and OTP request state.
 * - Channel selector locks during cooldown, unlocks after.
 * - ASCII only; no Unicode characters in comments or strings.
 */

// Imports
import { useMemo } from 'react';
import Otp from '../../contact/otp';
import CreatePassword from '../createpassword';
import { PasswordContext } from '../createpassword';
import { OtpContext } from '../../contact/otp';

// Component PasswordWithOtpLayout:
// Stateless layout composing password, channel selector, and OTP
export default function PasswordWithOtpLayout({ ...h }) {
    // Rendering - compound form structure
    return (
        <div className="space-y-3 md:space-y-6" aria-labelledby="password-with-otp-title">

            {/* Heading */}
            <h1 id="password-with-otp-title" className="text-base md:text-l text-neutral-300">
                {h.heading}
            </h1>

            {/* Password Component */}
            <PasswordContext.Provider value={useMemo(() =>
                ({ setPasswordState: h.setPasswordState }),
                [h.setPasswordState])}>
                <CreatePassword
                    auth={h.auth}
                    hideSecureMessage={true}
                    showChips={false}
                    showHeading={false}
                />
            </PasswordContext.Provider>

            <hr className='my-0.5 md:my-1 border-neutral-800' />

            {/* OTP Section with Channel Selection */}
            <div className="bg-neutral-800/30 rounded-lg p-4 space-y-3">
                {/* Warning text - channel-aware */}
                <p className="text-sm text-orange-300">
                    {h.warningText || `${h.heading} - OTP verification required`}
                </p>

                {/* Channel Selector - only when 2+ channels available */}
                {h.showChannelSelector && (
                    <ChannelSelector
                        channels={h.availableChannels}
                        selected={h.selectedChannel}
                        onSelect={h.setSelectedChannel}
                        locked={h.otpCooldown > 0}
                        hasRequested={h.otpHasRequested}
                    />
                )}

                {/* OTP Component */}
                <OtpContext.Provider value={useMemo(() =>
                    ({ setOtpState: h.setOtpState }),
                    [h.setOtpState])}>
                    <Otp
                        auth={h.auth}
                        isProceedReadyOtp={h.isProceedReadyOtp}
                        idNumber={h.idNumber}
                        cellNumber={h.cellNumber}
                        otpLength={6}
                        channel={h.selectedChannel}
                    />
                </OtpContext.Provider>
            </div>
        </div>
    );
}

// Channel display labels
const CHANNEL_LABELS = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' };

// Component ChannelSelector:
// Chip/pill buttons for selecting OTP delivery channel
function ChannelSelector({ channels, selected, onSelect, locked, hasRequested }) {
    // Derived: container classes for lock state
    const containerClasses = locked ? 'opacity-50 pointer-events-none' : '';

    // Rendering - label + chips row
    return (
        <div className={`flex items-center gap-2 ${containerClasses}`}>
            <span className="text-xs text-neutral-400">
                {hasRequested ? 'Sent via:' : 'Send via:'}
            </span>
            <div className="flex gap-1.5">
                {channels.map(ch => (
                    <button
                        key={ch}
                        type="button"
                        onClick={() => onSelect(ch)}
                        className={
                            ch === selected
                                ? 'px-2.5 py-0.5 rounded text-xs font-medium bg-emerald-600 text-white transition'
                                : 'px-2.5 py-0.5 rounded text-xs font-medium bg-neutral-800 text-neutral-400 hover:text-neutral-300 transition'
                        }
                    >
                        {CHANNEL_LABELS[ch] || ch}
                    </button>
                ))}
            </div>
        </div>
    );
}

// Component StatusChip:
// Individual status indicator chip
function StatusChip({ ok, warnWhenMissing = false, muted = false, children }) {
    let bgColor, textColor;

    if (muted) {
        bgColor = 'bg-neutral-800';
        textColor = 'text-neutral-500';
    } else if (ok) {
        bgColor = 'bg-emerald-600';
        textColor = 'text-white';
    } else if (warnWhenMissing) {
        bgColor = 'bg-orange-600';
        textColor = 'text-white';
    } else {
        bgColor = 'bg-neutral-800';
        textColor = 'text-neutral-400';
    }

    return (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor}`}>
            {children}
        </span>
    );
}
